<template>
  <div class="fbt">
    <div v-if="hasOriginals && !hideToggle" class="fbt__toggle-row">
      <button
        type="button"
        class="fbt__toggle"
        :class="{ 'fbt__toggle--active': showOriginalLocal }"
        @click.stop="showOriginalLocal = !showOriginalLocal"
      >
        <v-icon x-small class="mr-1">mdi-translate</v-icon>
        {{ showOriginalLocal ? "Übersetzung" : "Original" }}
      </button>
    </div>
    <div v-for="(entry, index) in entries" :key="index" class="fbt__entry">
      <div class="fbt__text">{{ displayText(entry) }}</div>
      <div v-if="entry.timestamp || entry.locale" class="fbt__meta">
        <span v-if="entry.timestamp">{{ timeAgo(entry.timestamp) }}</span>
        <span v-if="entry.locale"> · {{ entry.locale }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { getDateFromNow, decodeHtmlEntities } from "@/utils";

/**
 * Kunden-Feedback als sauberer Thread (neueste zuerst) auf Basis des
 * strukturierten feedback.comments-Arrays. Default: Übersetzung;
 * Umschalter zeigt den Original-Kundentext. Alt-Tasks ohne Array fallen
 * auf den zusammengesetzten Legacy-String zurück.
 */
export default {
  name: "FeedbackThread",
  props: {
    feedback: {
      type: Object,
      default: null,
    },
    /** Umschalter nicht selbst rendern (Elternteil zeigt ihn, z. B. in der Kopfzeile) */
    hideToggle: { type: Boolean, default: false },
    /** Von außen gesteuert: Originaltext statt Übersetzung */
    showOriginal: { type: Boolean, default: false },
  },
  data() {
    return {
      showOriginalLocal: false,
    };
  },
  computed: {
    entries() {
      const fb = this.feedback || {};
      if (Array.isArray(fb.comments) && fb.comments.length) {
        return [...fb.comments].reverse(); // neueste zuerst
      }
      if (fb.comment) {
        return [{ original: fb.commentOriginal || "", translated: fb.comment }];
      }
      return [];
    },
    hasOriginals() {
      return this.entries.some(
        e => e.original && e.translated && e.original !== e.translated
      );
    },
  },
  methods: {
    timeAgo(timestamp) {
      return getDateFromNow(timestamp);
    },
    /** Deutsche Kommentare IMMER im Original zeigen (Übersetzung DE→EN
     *  bringt dem Team nichts); sonst Default Übersetzung mit Umschalter */
    displayText(entry) {
      const isGerman =
        String(entry.locale || "").toLowerCase().startsWith("de") ||
        /^\(DE\)/.test(entry.translated || "");
      const text = isGerman
        ? entry.original || entry.translated
        : this.showOriginal || this.showOriginalLocal
          ? entry.original || entry.translated
          : entry.translated || entry.original;
      return decodeHtmlEntities(text);
    },
  },
};
</script>

<style lang="scss" scoped>
.fbt {
  &__toggle-row {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 4px;
  }

  &__toggle {
    display: inline-flex;
    align-items: center;
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: #fff;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 600;
    padding: 1px 8px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.55);

    .v-icon {
      color: inherit !important;
    }

    &--active {
      background: #eef0f3;
      color: rgba(0, 0, 0, 0.8);
    }
  }

  &__entry {
    background: #f6f7f9;
    border: 0;
    border-radius: 10px;
    padding: 8px 10px;
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 0;
    }

    /* Ältere Kommentare gedimmt — der neueste steht oben im Fokus */
    &:not(:first-child) {
      opacity: 0.65;
    }
  }

  &__text {
    font-size: 12.5px;
    line-height: 1.4;
    white-space: pre-wrap;
    word-break: break-word;
  }

  &__meta {
    font-size: 10.5px;
    color: rgba(0, 0, 0, 0.4);
    margin-top: 2px;
  }
}
</style>
